const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/utils/generalUtils.ts';
import Imm from 'immutable';
import { MessageDescriptor } from 'react-intl';
import moment, { Moment } from 'moment';
import textMap from './translations';
import _ from 'lodash';
import { Logger } from '../services/KisLoggerService';
import { KISIM_STD_DATE_TIME_FORMAT, KISIM_TIME_FORMAT } from '../defs/generalDefs';
import { AnyKisObjectPK, KisObjectKind } from '../defs/kisim';

const Log = Logger('generalUtils');

export const emptyMap = Imm.Map<string, any>();
export const emptyOrderedMap = Imm.OrderedMap<string, any>();
export const emptyList = Imm.List<any>();
export const emptySet = Imm.Set<string>();

export type MaybeMap = Imm.Map<string, any> | undefined;
export type MaybeOrderedMap = Imm.OrderedMap<string, any> | undefined;
export type MaybeList = Imm.List<any> | undefined;

type Messages = { [key: string]: MessageDescriptor };

export type ObjType =
    | 'Arzt'
    | 'Bezugsperson'
    | 'Falldetail'
    | 'Garant'
    | 'Gepart'
    | 'Image'
    | 'KisEvent'
    | 'KisFall'
    | 'KisOrder'
    | 'KisPDF'
    | 'KisReport'
    | 'KisSession'
    | 'KisTermin'
    | 'KisWork'
    | 'LarsOrder'
    | 'LarsReport'
    | 'MetaKat'
    | 'PatData'
    | 'PatForm'
    | 'Responsibility'
    | 'UniqueFallReport'
    | 'WorkItem'
    | 'msn'
    | 'order'
    | 'report';

/**
 * Checks whether a given value would evaluate to NULL when passing it to
 * KISAPI. QSCRIPT does not distinguish between undefined, null, '', {} or [].
 *
 * @return: true if the given value would evaluate to NULL; false if not.
 */
export function isQscriptNull(val: any) {
    if (typeof val === 'undefined' || val === null || val === '') {
        return true;
    }

    if (typeof val === 'object' && !Object.keys(val).length) {
        return true; // an empty object or array
    }

    return (Imm.Iterable as any).isIterable(val) && val.isEmpty();
}

/**
 * Helper function to ensure the given record contains at least the given
 * members (shallow). If any of the listed members is missing or empty (in
 * the QSCRIPT sense), an exception will be raised.
 */
export function checkMandatoryMembers(rec: Imm.Map<string, any>, members: string[]) {
    members.forEach(key => {
        if (isQscriptNull(rec.get(key))) {
            throw new Error(`Mandatory member missing or empty: ${key}`);
        }
    });
}

/**
 * Helper function to ensure the given record doesn't contain any of the given
 * members (shallow). If any of the listed member is present and non-empty (in
 * the QSCRIPT sense), an exception will be raised.
 */
export function checkEmptyMembers(rec: Imm.Map<string, any>, members: string[]) {
    members.forEach(key => {
        if (!isQscriptNull(rec.get(key))) {
            throw new Error(`Member must either be empty or omitted: ${key}`);
        }
    });
}

/**
 * Helper function to ensure a modified record does not change any of the given members (shallow). If any of the listed members is
 * changed in recChanged, an exception will be raised.
 */
export function checkMembersUnchanged(
    recOriginal: Imm.Map<string, any>,
    recChanged: Imm.Map<string, any>,
    members: string[]
) {
    const oldSkel = Imm.fromJS({});
    const sendSkel = Imm.fromJS({});
    members.forEach((member: string) => {
        oldSkel.set(member, recOriginal.get(member));
        sendSkel.set(member, recChanged.get(member));
    });

    if (!Imm.is(oldSkel, sendSkel)) {
        throw new Error(
            `Refusing to update record, because the members: ${JSON.stringify(
                oldSkel.toJS()
            )} should not change: ${JSON.stringify(sendSkel.toJS())}`
        );
    }
}

/**
 * Helper function to filter out all elements from a value map that have not changed from a given original.
 */
export function eliminateUnchanged(
    recOriginal: Imm.Map<string, any>,
    recChanged: Imm.Map<string, any>,
    except: string[]
): Imm.Map<string, any> {
    return recChanged.filterNot((value: any, key: string) => {
        return except.indexOf(key) < 0 && Imm.is(value, recOriginal.get(key));
    }) as Imm.Map<string, any>;
}

/**
 * Generates a string that uniquely identifies the given KISIM object. The
 * uniqueness is guaranteed within the object's kind only.
 * @param kind (in): The kind of KISIM object.
 * @param pk (in): The KISIM object's primary key.
 * @return: A string uniquely identifying the KISIM object.
 */
export function idForKisObject(kind: KisObjectKind, pk: AnyKisObjectPK): string {
    const k = pk as any;

    switch (kind) {
        case KisObjectKind.Patient:
            return `${k.PATNR}`;
        case KisObjectKind.Case:
            return `${k.FALLNR}`;
        case KisObjectKind.Order:
            return `${k.SENDER}_${k.ORDERNR}`;
        case KisObjectKind.Event:
            return `${k.SENDER}_${k.EVENTNR}`;
        case KisObjectKind.MetaKat:
            return `${k.ICODE}`;
        case KisObjectKind.Report:
            return `${k.SENDER}_${k.REPORTNR}`;
        case KisObjectKind.LabReport:
            return `${k.SENDER}_${k.REPORTNR}`;
        case KisObjectKind.Termin:
            return `${k.TERMNR}`;
        case KisObjectKind.WorkItem:
            return `${k.WORKID}`;
        default:
            throw new Error(`Unexpected value for kind: ${kind}`);
    }
}

/**
 * Rounds a given moment date/time down to the given accuracy.
 * @param mom Specifies the moment to be rounded. This object is left unchanged.
 * @param accuracy One of 'years', 'months', 'days', 'hours', 'minutes'.
 * @return: A new moment object
 */
export function momentFloor(mom: Moment, accuracy: string): Moment {
    switch (accuracy) {
        case 'years':
        case 'year':
            return moment(mom.format('YYYY-01-01'), 'YYYY-01-01');

        case 'months':
        case 'month':
            return moment(mom.format('YYYY-MM-01'), 'YYYY-MM-01');

        case 'days':
        case 'day':
            return moment(mom.format('YYYY-MM-DD'), 'YYYY-MM-DD');

        case 'hours':
        case 'hour':
            return moment(mom.format('YYYY-MM-DD HH:00'), 'YYYY-MM-DD HH:00');

        case 'minutes':
        case 'minute':
            return moment(mom.format('YYYY-MM-DD HH:mm'), 'YYYY-MM-DD HH:mm');

        default:
            throw new Error(`Accuracy not handled: ${accuracy}`);
    }
}

/**
 * Attempts to recognize the given date's format, and converts it to the default format.
 * @return The date as YYYY-MM-DD HH:mm:ss, or null if the format was not recognized.
 */
export function normalizeDate(dat: string): string | null {
    let mom: Moment;

    if (dat.length === 19 && dat[2] === '.') {
        mom = moment(dat, 'DD.MM.YYYY HH:mm:ss');
    } else if (dat.length === 16 && dat[2] === '.') {
        mom = moment(dat, KISIM_STD_DATE_TIME_FORMAT);
    } else if (dat.length === 10 && dat[2] === '.') {
        mom = moment(dat, 'DD.MM.YYYY');
    } else if (dat.length === 5 && dat[2] === ':') {
        mom = moment(dat, 'HH:mm');
    } else {
        // Let moment do the guesswork.
        mom = moment(dat);
    }

    return !mom.isValid() ? null : mom.format(KISIM_TIME_FORMAT);
}

/**
 * Creates a true deep clone of any object
 */
export const createDeepClone = (value: any): any => {
    if (typeof value !== 'object' || value === null) {
        return value;
    }
    if (value?._isAMomentObject) {
        return value.clone();
    }
    const ret = Array.isArray(value) ? Object.assign([], value) : Object.assign({}, value);
    Object.keys(ret).forEach((key: any) => {
        ret[key] = createDeepClone(ret[key]);
    });
    return ret;
};

export const recurseCheck = (a: any, b: any): boolean => {
    return recurseCheckVerboseHelper(a, b, true);
};

const recurseCheckVerboseHelper = (
    a: any,
    b: any,
    undefEqualEmptyStr: boolean = false,
    path: string = '',
    messages: string[] = []
): boolean => {
    if (undefEqualEmptyStr && (a === '' || typeof a === 'undefined') && (b === '' || typeof b === 'undefined')) {
        return true;
    }
    if (typeof a !== typeof b || Array.isArray(a) !== Array.isArray(b)) {
        const atype: string = Array.isArray(a) ? 'array' : typeof a;
        const btype: string = Array.isArray(b) ? 'array' : typeof b;
        messages.push(`Expected @${path}: type ${atype}, found: ${btype}`);
        return false;
    }
    if (a === null || b === null) {
        return a === null && b === null;
    }

    if (_.isFunction(a) && _.isFunction(b)) {
        // even if toString is the same, it is not guaranteed to be truly equal...
        if (a === b || a.toString() === b.toString()) {
            return true;
        }
        messages.push(`@${path}: different functions`);
        return false;
    }

    if (Array.isArray(a)) {
        let result = true;
        for (let idx = 0; idx < Math.max(a.length, b.length); idx++) {
            result =
                recurseCheckVerboseHelper(
                    a[idx],
                    b[idx],
                    undefEqualEmptyStr,
                    `${path}${path === '' ? '' : '.'}${idx}`,
                    messages
                ) && result;
        }
        return result;
    }
    if (typeof a === 'object') {
        if (a._isAMomentObject) {
            if (!b._isAMomentObject) {
                return false;
            }
            return a.isSame(b);
        }

        let result = true;
        Object.keys(Object.assign({}, a, b)).forEach((key: string) => {
            result =
                recurseCheckVerboseHelper(
                    a[key],
                    b[key],
                    undefEqualEmptyStr,
                    `${path}${path === '' ? '' : '.'}${key}`,
                    messages
                ) && result;
        });
        return result;
    }
    if (a !== b) {
        messages.push(`Expected @${path}: value ${JSON.stringify(a)}, found: ${JSON.stringify(b)}`);
        return false;
    }
    return true;
};

export const recurseCheckVerbose = (a: any, b: any): boolean => {
    const messages: string[] = [];
    const result = recurseCheckVerboseHelper(a, b, true, '', messages);
    if (!result) {
        messages.forEach((line: string) => {
            console.log(line);
        });
    }
    return result;
};

const extensionDontCheckFor = [
    '__preparationMetadata',
    'coreTransformationErrors',
    'terminIntNr',
    'blockRemark',
    'blockSubject',
    'blockAttendees',
];
const extendedDebugging = true;
export const recurseCheckExtension = (b: any, a: any): boolean => {
    const messages: string[] = [];
    const result = recurseCheckVerboseHelper(
        _.omit(a, extensionDontCheckFor),
        _.omit(b, extensionDontCheckFor),
        true,
        '',
        messages
    );
    if (!result) {
        messages.forEach((line: string) => {
            console.log(line);
        });
        if (extendedDebugging) {
            console.log('HAVE', JSON.stringify(_.omit(b, extensionDontCheckFor), null, 2));
            console.log('EXPECTED', JSON.stringify(_.omit(a, extensionDontCheckFor), null, 2));
        }
    }
    return result;
};

export const deepIndexOf = (array: any, value: any): number => {
    if (!Array.isArray(array)) {
        return -1;
    }
    for (let i = 0; i < array.length; i++) {
        if (recurseCheck(array[i], value)) {
            return i;
        }
    }
    return -1;
};

/**
 * Complexity is O(m * n) in the worst case
 * @return true if the two arrays have at least one element in common
 */
export function hasArrayIntersect<T>(a1: T[], a2: T[]): boolean {
    for (const v1 of a1) {
        for (const v2 of a2) {
            if (v1 !== undefined && v1 !== null && v1 === v2) {
                return true;
            }
        }
    }

    return false;
}

export const mergeChangesInto = (target: any, pre: any, post: any, path: string = ''): any => {
    if (typeof pre !== typeof post || Array.isArray(pre) !== Array.isArray(post)) {
        return false;
    }
    if (Array.isArray(pre)) {
        for (const thePre of pre) {
            if (deepIndexOf(post, thePre) < 0) {
                if (!Array.isArray(target)) {
                    return false;
                }
                const tIdx = deepIndexOf(target, thePre);
                if (tIdx >= 0) {
                    target.splice(tIdx, 1);
                }
            }
        }
        for (const thePost of post) {
            if (deepIndexOf(pre, thePost) < 0) {
                if (!Array.isArray(target)) {
                    return false;
                }
                target.push(thePost);
            }
        }
        return true;
    }
    if (typeof pre === 'object' && pre !== null) {
        const keys = Object.keys(Object.assign({}, pre, post));
        if (typeof target !== 'object') {
            for (const key of keys) {
                if (!mergeChangesInto({}, pre[key], post[key], `${path}.${key}`)) {
                    return false;
                }
            }
        } else {
            for (const key of keys) {
                if (
                    Array.isArray(pre[key]) &&
                    Array.isArray(post[key]) &&
                    !recurseCheck(pre[key], post[key]) &&
                    typeof target[key] === 'object' &&
                    !Array.isArray(target[key]) &&
                    Object.keys(target[key]).filter(v => !v.match(/^\d+$/)).length < 1
                ) {
                    target[key] = Object.keys(target[key]).map((akey: string) => target[key][akey]);
                }
                if (!mergeChangesInto(target[key], pre[key], post[key], `${path}.${key}`)) {
                    target[key] = post[key];
                }
            }
        }
        return true;
    }
    return pre === post;
};

/**
 * In KISIM numbers can reference to a time unit. Use this object to get the correct unit as word.
 */
export const timeUnitsKisim: Messages = {
    unit_2: textMap.unit_2,
    unit_3: textMap.unit_3,
    unit_4: textMap.unit_4,
    unit_5: textMap.unit_5,
    unit_6: textMap.unit_6,
    unit_7: textMap.unit_7,
    unit_8: textMap.unit_8,
};

/**
 * Create a valid key to get the text from `timeUnitsKisim`
 */
export function buildTimeUnitsKisimId(unit: string): string {
    const unitParsed = Number.parseInt(unit, 10);
    if (unitParsed >= 2 && unitParsed <= 8) {
        return `unit_${unitParsed}`;
    }
    throw new Error(`Received invalid value for unit: ${unit}`);
}

/**
 * Main ATC categories (depending on first letter of ATC code)
 */
export const ATCMainCategories: Messages = {
    A: textMap.A,
    B: textMap.B,
    C: textMap.C,
    D: textMap.D,
    G: textMap.G,
    H: textMap.H,
    J: textMap.J,
    L: textMap.L,
    M: textMap.M,
    N: textMap.N,
    P: textMap.P,
    R: textMap.R,
    S: textMap.S,
    V: textMap.V,
    otherATCCode: textMap.otherATCCode,
    noATCCode: textMap.noATCCode,
};

/**
 * Create a valid key to get the text from `ATCMainCategories`
 */
export function buildATCMainCategoriesId(category: string): string {
    const categoryUpper = category.toUpperCase();
    switch (categoryUpper) {
        case 'A':
        case 'B':
        case 'C':
        case 'D':
        case 'G':
        case 'H':
        case 'J':
        case 'L':
        case 'M':
        case 'N':
        case 'P':
        case 'R':
        case 'S':
        case 'V':
            return categoryUpper;
        case '###EMPTY':
            return 'noATCCode';
        default:
            return 'otherATCCode';
    }
}

/**
 * Determines the CSS class name to be used for displaying a gender symbol.
 * @param gender A string, supported values are 'M' (masculine), 'W' (feminine), '', null (gender unknown)
 * NOTE: The values of gender must match the values from KISAPI! (PATIENT.SEX)
 */
export function iconForGender(gender: string) {
    switch (gender) {
        case 'M':
            return 'icons8-kis-male';
        case 'W':
            return 'icons8-kis-female';
        default:
            return 'icons8-kis-genderless';
    }
}

/**
 * Encode an object to an x-www-form-urlencoded string to be used
 * in POST request. The speciality of this encoding is that %20 need to be handled
 * as + characters.
 * @param val contains the object or the string to be converted
 * @return the encoded string
 */
export function encodeXWwwFormUrlencoded(val: any): string {
    const pairs = _.toPairs(val) as [param: string, value: string][];
    const paramsWithValuesArray = pairs.map(pair => {
        const paramValueArray = pair.map(item => {
            const encoded = encodeURIComponent(item);
            return encoded.replace(/%20/g, '+');
        });

        return paramValueArray.join('=');
    });
    return paramsWithValuesArray.join('&');
}

/**
 * Keep this functionally identical to assert.ok() from 'node:assert'.
 */
export function assert(condition: unknown, msg: string = ''): asserts condition {
    if (!condition) {
        throw new Error('Assertion failed' + (!msg ? '' : `: ${msg}`));
    }
}

export function assertNever(_x: never, msg = 'Error: assertNever'): never {
    throw new Error(`${msg}: _x is ${_x}`);
}

/**
 * Traverse objects recursively and converts its values of type moment to a string. If keys are given, only attributes of that key are
 * traversed and converted respectively.
 * @param obj       Object to travers and whose values will be converted.
 * @param keys      Keys of the given object which should be handled.
 * @returns {any}   obj with converted values (values of type moment converted to string)
 */
export function replaceMomentsByString(obj: any, keys?: string[]): any {
    /**
     * Handles values of objects:
     *  - object: is traversed
     *  - moment: convert to string
     *  - every thing else is returned untouched
     * @param value
     * @returns {any}
     */
    const handleValue = (value: any): any => {
        if (!_.isEmpty(value)) {
            if (value instanceof moment) {
                return moment(value).format(KISIM_TIME_FORMAT);
            }

            if (typeof value === 'object') {
                return replaceMomentsByString(value);
            }
        }
        return value;
    };

    if (!_.isEmpty(obj)) {
        if (keys && !_.isEmpty(keys)) {
            keys.forEach((key: string) => {
                const value = (obj as any)[key];
                if (!_.isEmpty(value)) {
                    (obj as any)[key] = handleValue(value) as any;
                }
            });
        } else {
            return _.mapValues(obj, (value: any) => handleValue(value));
        }
    }
    return obj;
}

/**
 * Converts the given key from QSCRIPT convention (UpperCamelCase) to JavaScript convention
 * (lowerCamelCase).
 */
export function convertKeyToLowerCase(key: string): string {
    if (!key) {
        return key; // return whatever we were given
    }

    key = `${key}`; // make sure key is a string

    if (key.toUpperCase() === key) {
        return key.toLowerCase();
    }

    return `${key.charAt(0).toLowerCase()}${key.slice(1)}`;
}

/**
 * Creates a new map by recursively iterating through the given map and replacing keys in QSCRIPT convention
 * (UpperCamelCase) to keys in JavaScript convention (lowerCamelCase). Number keys will be converted to string keys in
 * QSCRIPT fashion (starting from 1). If a predicate method is given, the method will not descend into any subtree for
 * whose root the predicate returns false. Note that the key of such a subtree will be converted, though.
 * @return a map containing the same values under possibly new keys
 */
export function convertKeysToLowerCase(
    iterable: Imm.Iterable<unknown, any>,
    predicate?: (subtree: any, key: string) => boolean
): Imm.Map<string, any> {
    let result = Imm.Map<string, any>();

    iterable.forEach((val: any, oldKey: unknown) => {
        const strKey =
            typeof oldKey === 'string'
                ? oldKey // iterable is probably a Map
                : typeof oldKey === 'number'
                  ? `${oldKey + 1}` // iterable is a List; +1 for QSCRIPT convention
                  : `${oldKey}`; // iterable is something else (should not usually happen)

        const newKey = convertKeyToLowerCase(strKey);

        if (!predicate || predicate(val, strKey)) {
            // NOTE: We MUST NOT check val.constructor.name, because it differs in production builds!
            // Instead, use Imm.Iterable.isIterable!

            if (typeof val === 'object' && Imm.Iterable.isIterable(val)) {
                val = convertKeysToLowerCase(val, predicate); // recursion
            }
        }

        result = result.set(newKey, val);
    });

    return result;
}

/**
 * Deeply clones the given data, zapping problematic keys and limiting the depth.
 * @param data data to be trimmed
 * @param zapProblematicKeysBeyondDepth minimum depth required for zapping problematic keys
 * @param problematicKeys an array of problematic keys; must be defined, but may be empty
 * @param zapIterablesBeyondDepth minimum depth required for zapping subtrees
 * @param zapWith zapped members will be replaced with this (null, a string message, etc.)
 * @param curDepth Recursion depth. Callers should not set this argument.
 */
export function cloneAndZap(
    data: any,
    zapProblematicKeysBeyondDepth: number,
    problematicKeys: string[],
    zapIterablesBeyondDepth: number,
    zapWith: any,
    curDepth: number = 0
): any {
    if (!data || typeof data === 'string' || typeof data === 'number') {
        return data; // not an iterable
    }

    if (typeof data !== 'object') {
        return zapWith; // we don't know about this kind of thing
    }

    if (curDepth > zapIterablesBeyondDepth) {
        return zapWith; // we're in too deep
    }

    if (Array.isArray(data)) {
        return data.map((entry: any) => {
            return cloneAndZap(
                entry,
                zapProblematicKeysBeyondDepth,
                problematicKeys,
                zapIterablesBeyondDepth,
                zapWith,
                curDepth + 1
            );
        });
    } else if (Imm.Iterable.isIterable(data)) {
        return data.map((entry: any, idx: any) => {
            if (curDepth >= zapProblematicKeysBeyondDepth && problematicKeys.indexOf(idx) >= 0) {
                return zapWith;
            } else {
                return cloneAndZap(
                    entry,
                    zapProblematicKeysBeyondDepth,
                    problematicKeys,
                    zapIterablesBeyondDepth,
                    zapWith,
                    curDepth + 1
                );
            }
        });
    } else {
        const clone: any = {};

        for (const key in data) {
            if (data.hasOwnProperty(key)) {
                if (curDepth >= zapProblematicKeysBeyondDepth && problematicKeys.indexOf(key) >= 0) {
                    clone[key] = zapWith;
                } else {
                    clone[key] = cloneAndZap(
                        data[key],
                        zapProblematicKeysBeyondDepth,
                        problematicKeys,
                        zapIterablesBeyondDepth,
                        zapWith,
                        curDepth + 1
                    );
                }
            }
        }

        return clone;
    }
}

export const over = (path: string | string[], func: (value: any) => any, data: any) =>
    _.set(data, path, func(_.get(data, path)));

/**
 * Estimates a JS object's size in memory. Use this for analysing parts of the Redux state to see which branches
 * might need some overhaul in terms of memory footprint.
 * @param something any JS object to estimate
 * @param depth depth of recursion; callers should pass null
 * @return Estimated number of bytes occupied by the object. Do not rely on this value to be accurate!
 */
export function estimateSizeInMemory(something: any, depth: number | null = null): number {
    if (!depth) {
        try {
            return estimateSizeInMemory(something, 1);
        } catch (e) {
            Log.error(`estimateSizeInMemory failed with exception: ${e.message}`, e.stack);
            return -1;
        }
    }

    if (depth > 1000) {
        throw new Error('Either this object is very deep, or it contains at least one circular reference!');
    }

    if (something === undefined || something === null || something === '' || typeof something === 'boolean') {
        return 1;
    }

    if (typeof something === 'string') {
        return something.length * 2; // assuming internal representation is utf-16
    }

    if (typeof something === 'number') {
        return 8; // assuming 64bit ints or 64bit doubles
    }

    if (typeof something === 'function') {
        return 8; // just add the size of a 64bit reference pointer, assuming the function is defined elsewhere
    }

    if (typeof something !== 'object') {
        throw new Error(`Don't know how to handle ${typeof something}!`);
    }

    if (Array.isArray(something)) {
        let sum = 16; // estimated overhead of any array (e.g. its length, prototype, etc.)

        something.forEach((member: any) => {
            sum += estimateSizeInMemory(member, depth + 1);
        });

        return sum;
    } else {
        let sum = 16; // estimated overhead of any object (e.g. its prototype, hashmap, etc.)

        // tslint:disable-next-line:forin
        for (const key in something) {
            sum += 2; // estimated overhead of the hashmap entry
            // noinspection JSUnfilteredForInLoop
            sum += estimateSizeInMemory(key, depth + 1);
            // noinspection JSUnfilteredForInLoop
            sum += estimateSizeInMemory(something[key], depth + 1);
        }

        return sum;
    }
}

/**
 * Converts the given number of bytes to a human-readable string.
 * @param size number of bytes
 * @return a string
 */
export function humanizeByteSize(size: number): string {
    if (size < 1000) {
        return `${size} B`;
    }

    size = Math.floor(size / 100) / 10;

    if (size < 1000) {
        return `${size} KB`;
    }

    size = Math.floor(size / 100) / 10;

    if (size < 1000) {
        return `${size} MB`;
    }

    size = Math.floor(size / 100) / 10;
    return `${size} GB`;
}

/**
 * _.isEmpty does not work properly for immutables or numbers :(
 *
 * It works according to its specification ;)
 * Can a number be empty? ...
 * If it doesn't work for immutable, another utility should be used, or we should just check on size...
 */
export function isEmpty(something: any): boolean {
    if (!something) {
        return true;
    } else if (typeof something !== 'object') {
        return false;
    } else if (Array.isArray(something)) {
        return something.length === 0;
    } else if (Imm.Iterable.isIterable(something)) {
        return typeof something.size === 'number' && something.size === 0;
    } else {
        return Object.keys(something).length === 0;
    }
}

/**
 * KISAPI uses "figure dash" signs (-) instead of "en dash" (\u2013), because QSCRIPT is incapable of Unicode.
 * This function will replace "figure dash by "en dash"
 * @param str String to check for figure dash and replace it
 */
export function prettifyDashInString(str: string): string {
    return str.replace('-', '\u2013');
}

/**
 * Helper function to create a link structure pointing to a KISIM object.
 * The supported keys is a combination of the keys used by KISIM's GetRecordType and patdash's configuration types.
 * @return A Map containing the link structure, or null if we do not support this kind of object.
 */
export function makeObjLink(
    objType: ObjType,
    partialRec: Imm.Map<string, any> | undefined
): Imm.Map<string, any> | undefined {
    if (!partialRec || !partialRec.size) {
        return undefined;
    }

    let linkType = objType;
    let pk = emptyMap;

    // The reason why multiple objTypes lead to the same linkType is because our cardDefs
    // sometimes uses the KISIM record type and sometimes its own type.

    switch (linkType) {
        case 'KisEvent':
        case 'msn':
            linkType = 'KisEvent';
            pk = Imm.fromJS({
                SENDER: partialRec.get('SENDER'),
                EVENTNR: partialRec.get('EVENTNR'),
            });
            break;
        case 'LarsOrder':
            pk = Imm.fromJS({
                SENDER: partialRec.get('SENDER'),
                ORDERNR: partialRec.get('ORDERNR'),
            });
            break;
        case 'LarsReport':
            pk = Imm.fromJS({
                SENDER: partialRec.get('SENDER'),
                REPORTNR: partialRec.get('REPORTNR'),
            });
            break;
        case 'KisOrder':
        case 'order':
            linkType = 'KisOrder';
            pk = Imm.fromJS({
                SENDER: partialRec.get('SENDER'),
                ORDERNR: partialRec.get('ORDERNR'),
            });
            break;
        case 'KisReport':
        case 'report':
            // Diagnoses refer to their report with Sender/ReportNr rather than SENDER/REPORTNR.
            linkType = 'KisReport';
            pk = Imm.fromJS({
                SENDER: partialRec.get('SENDER') || partialRec.get('Sender'),
                REPORTNR: partialRec.get('REPORTNR') || partialRec.get('ReportNr'),
            });
            break;
        case 'KisSession':
            pk = Imm.fromJS({
                SENDER: partialRec.get('SENDER'),
                REPORTNR: partialRec.get('REPORTNR'),
            });
            break;
        case 'KisTermin':
            pk = Imm.fromJS({
                TERMNR: partialRec.get('TERMNR'),
            });
            break;
        case 'Responsibility':
        case 'KisWork':
        case 'WorkItem':
            linkType = 'WorkItem';
            pk = Imm.fromJS({
                WORKID: partialRec.get('WORKID'),
            });
            break;
        case 'KisFall':
            linkType = 'KisFall';
            pk = Imm.fromJS({
                FALLNR: partialRec.get('FALLNR'),
            });
            break;
        case 'Image':
            pk = Imm.fromJS({
                SENDER: partialRec.get('SENDER'),
                FREMDNR: partialRec.get('FREMDNR'),
            });
            break;
        case 'MetaKat':
            pk = Imm.fromJS({
                ICODE: partialRec.get('ICODE'),
            });
            break;
        case 'Garant':
        case 'Gepart':
            pk = Imm.fromJS({
                OBJECTKAT: partialRec.get('OBJECTKAT'),
                OBJECTKEY: partialRec.get('OBJECTKEY'),
            });
            break;
        case 'PatData':
        case 'PatForm':
            linkType = 'PatForm';
            pk = Imm.fromJS({
                PATNR: partialRec.get('PATNR'),
            });
            break;
        case 'Falldetail':
            pk = Imm.fromJS({
                PATNR: partialRec.get('PATNR'),
                FALLNR: partialRec.get('FALLNR'),
                DETAILNR: partialRec.get('TERMNR'),
            });
            break;
        case 'Bezugsperson':
        case 'Arzt':
            linkType = 'UniqueFallReport';
            pk = Imm.fromJS({
                FCODE: 'SFALINFO01',
            });
            break;
        case 'UniqueFallReport':
            pk = Imm.fromJS({
                FCODE: partialRec.get('FCODE'),
                PATNR: partialRec.get('PATNR'),
                FALLNR: partialRec.get('FALLNR'),
            });
            break;
        case 'KisPDF':
            pk = Imm.fromJS({
                SENDER: partialRec.get('SENDER'),
                ICODE: partialRec.get('ICODE'),
            });
            break;
        default:
            // No assertNever here, because we need to gracefully handle the case
            // when KISAPI told us an objType we're not prepared for.
            return undefined;
    }

    const hasAnyMissingMembers = !pk.size || pk.filter(e => !e).size > 0;

    if (hasAnyMissingMembers) {
        return undefined;
    }

    return pk.set('Type', linkType);
}

/**
 * Sorts the given iterable by key, either numerically or alphabetically, depending on the keys.
 */
export function sortByKey<K, V>(iterable: Imm.Iterable<K, V>): Imm.Iterable<K, V> {
    let keysAreNumerical = true;

    iterable.forEach((_, key) => {
        const n = Number(key);
        if (!Number.isFinite(n)) keysAreNumerical = false;
    });

    if (keysAreNumerical) {
        // If all keys are numerical, we sort numerically.
        return iterable.sortBy((_, key) => Number(key));
    } else {
        // If there is at least one key that is not numerical, we sort alphabetically, ignoring case.
        return iterable.sortBy((_, key) => `${key}`.toLowerCase());
    }
}

/**
 * Clears an object's members.
 */
export function clear(obj: Record<string, unknown>) {
    Object.keys(obj).forEach(key => {
        delete obj[key];
    });
}

/**
 * Compares two elements and builds a nested structure that shows the difference.
 * @param oldValue Original structure
 * @param newValue New structure
 * @param removed Optional value that will be used to indicate when a member has been deleted
 * @return A structure that omits all members that are identical, and has members that were added, removed, or changed
 */
export function getDiff(oldValue: any, newValue: any, removed: any = null): any {
    if (typeof oldValue !== 'object' || typeof newValue !== 'object' || !oldValue || !newValue) {
        return oldValue === newValue ? undefined : newValue;
    }

    if (Array.isArray(oldValue) !== Array.isArray(newValue)) {
        return newValue;
    }

    if (Array.isArray(oldValue) && Array.isArray(newValue)) {
        const result: any[] = [];

        newValue.forEach((nval: any, key: unknown) => {
            if (typeof key !== 'number') throw new Error('WTF');
            const d = getDiff(oldValue[key], nval, removed);
            result.push(d);
        });

        oldValue.forEach((_oval: any, key: number) => {
            if (key > newValue.length - 1) {
                result.push(removed);
            }
        });

        return !result.filter(e => e !== undefined).length ? undefined : result;
    } else {
        const result: any = {};
        let oldKeys = Object.keys(oldValue);

        Object.keys(newValue).forEach(key => {
            const d = getDiff(oldValue[key], newValue[key], removed);
            if (d !== undefined) result[key] = d;
            oldKeys = oldKeys.filter(k => k !== key);
        });

        oldKeys.forEach(key => {
            result[key] = removed;
        });

        return !Object.keys(result).length ? undefined : result;
    }
}
